import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { ContentPlanRootState as S } from '../../elements/contentPlan/store';
import { ElementAction } from '../../elements/elementAction';
import { Functions } from '../../types/types';
import { View } from '../../types/View';
import { ViewModalState, updateViewModalState } from './viewModalReducer';
import { clearAlert, setError } from '../Alert/alertAction';
import {
  reloadWorkspace,
  selectWorkspace
} from '../ContentPlanning/contentPlanningActions';
import { setLoading } from '../Loader/loaderAction';
import { Page } from '../../enums/Page';
import { ViewType } from '../../enums/ViewType';
import { NodeType } from '../../enums/NodeType';
import { updatePlanningState } from '../ContentPlanning/contentPlanningReducer';
import { updateSelectedView } from '../View/viewReducer';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { excludeEmptyFilters, cleanFilter } from '../../utils';
import { SetURLSearchParams } from 'react-router-dom';

const cloneView =
  (
    view: View.Setting,
    folderRefId?: string,
    setSearchParams?: SetURLSearchParams
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { planning } = getState();
    const { cloneView, getWorkspace } = getAPI();
    const refId = view?.props?.refId;
    const workspaceRefId = planning?.selectedWorkspace?.props?.refId;
    if (refId && workspaceRefId) {
      try {
        dispatch(setLoading(true));
        const cloned = await cloneView({ refId, folderRefId, workspaceRefId });
        const workspace = await getWorkspace(workspaceRefId);
        if (cloned?.props?.refId && setSearchParams) {
          setSearchParams({ v: cloned.props.refId });
        }
        dispatch(updatePlanningState({ selectedWorkspace: workspace }));
        dispatch(selectWorkspace(workspaceRefId, cloned?.props?.refId));
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

const cloneFolder =
  (folderRefId: string): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { planning } = getState();
    const { cloneFolder, getWorkspace } = getAPI();
    const workspaceRefId = planning?.selectedWorkspace?.props?.refId;
    if (folderRefId && workspaceRefId) {
      try {
        dispatch(setLoading(true));
        const cloned = await cloneFolder({ folderRefId, workspaceRefId });
        const workspace = await getWorkspace(workspaceRefId);
        dispatch(updatePlanningState({ selectedWorkspace: workspace }));
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

const save =
  (): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { viewModal, planning, view: viewState } = getState();
    const {
      viewType,
      card,
      page,
      folderRefId,
      isMyItemsView,
      isMyExpiryDashboard,
      parentView,
      ...props
    } = viewModal;
    let view = { props, parent: parentView } as View.Setting;
    view.props.type = viewType;
    if (view.props.type == ViewType.Tabular) {
      props.groupBy = null;
    }
    if (props.groupBy) {
      view.props.groupBy = props.groupBy.filter(g => !!g.field);
      view.props.groupBy.forEach(g => {
        if (!g.groupByParentField) {
          delete g.groupByParentField;
          delete g.groupByParentOrderBy;
        }
      });
    }
    if (view.props.preApplyFilters) {
      view.props.preApplyFilters = excludeEmptyFilters(
        view.props.preApplyFilters
      );
      view.props.preApplyFilters = cleanFilter(view.props.preApplyFilters);
    }
    if (view.props.filters) {
      view.props.filters = cleanFilter(view.props.filters, ['uuid']);
    }

    if (isMyExpiryDashboard) {
      // Add back the 12 month filter
      if (
        !view.props.preApplyFilters?.filter(f => f.field == 'Item.expiryDate')
          .length
      ) {
        const expiryFilter = {
          field: 'Item.expiryDate',
          conditions: [{ relative: { from: 'TODAY', to: 'P12M' } }]
        };
        view.props.preApplyFilters?.push(expiryFilter);
      }
    }
    const workspaceRefId = planning?.selectedWorkspace?.props?.refId;
    const { createView, updateView } = getAPI();
    try {
      dispatch(setLoading(true, 'viewModalLoader'));
      const upsertFn = view.props.refId ? updateView : createView;
      const results = await upsertFn({
        workspaceRefId,
        view,
        folderRefId
      });
      if (results?.length) {
        const viewRefId = results[0].props?.refId;
        const selectedViewrefId = viewState.selected?.props?.refId;
        if (selectedViewrefId != viewRefId) {
          await dispatch(reloadWorkspace(workspaceRefId, viewRefId));
        } else {
          dispatch(updateSelectedView(null));
          dispatch(reloadWorkspace(workspaceRefId, viewRefId));
        }
        dispatch(closeViewModal());
        if (selectedViewrefId != viewRefId) {
          return viewRefId;
        }
      } else {
        throw new Error(
          `Failed to upsert view, please contact CPP Help if this issue persists.`
        );
      }
    } catch (err) {
      dispatch(setError(err.message, 'viewModalAlert'));
    } finally {
      dispatch(setLoading(false, 'viewModalLoader'));
    }
  };

const deleteView =
  (view: View.Setting): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { deleteView } = getAPI();
    try {
      dispatch(setLoading(true));
      const results = await deleteView({ refId: view?.props?.refId });
      if (results?.length) {
        dispatch(reloadWorkspace());
      } else {
        throw new Error(
          `Failed to delete view, please contact CPP Help if this issue persists.`
        );
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

const openViewModal =
  (view?: View.Setting, folder?: View.Folder): ElementAction<Functions, S> =>
  dispatch => {
    const folderRefId = folder?.props?.refId;
    if (view) {
      const v = JSON.parse(JSON.stringify(view));
      if (v?.props?.preApplyFilters?.length) {
        v.props.preApplyFilters = v?.props?.preApplyFilters.map(f => ({
          ...f,
          uuid: Math.random().toString(10)
        }));
      }
      if (v?.props?.filters?.length) {
        v.props.filters = v?.props?.filters.map(f => ({
          ...f,
          uuid: Math.random().toString(10)
        }));
      }

      dispatch(
        updateViewModalState({
          page: Page.Setup,
          ...(v.props || {}),
          viewType: v.props.type,
          card: v.card,
          parentView: v.parent,
          isMyItemsView: v.isMyItemsView,
          folderRefId: folderRefId
        })
      );
    } else {
      dispatch(
        updateViewModalState({
          page: Page.Setup,
          viewType: ViewType.Kanban,
          fields: [
            'Item.title',
            'Item.itemId',
            'Item.role',
            'Item.primaryQuestion',
            'Item.taxonomyTag',
            'Item.rolePriority',
            'Item.priorityJourneyStep',
            'Item.status',
            'Item.author'
          ],
          isMyItemsView: false,
          folderRefId: folderRefId,
          data: NodeType.Item
        })
      );
    }
  };

const closeViewModal = (): ElementAction<Functions, S> => dispatch => {
  dispatch(updateViewModalState({ page: null }));
  dispatch(clearAlert('viewModalAlert'));
};

const goToPage =
  (pages: Pages, p: string | 1 | -1): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const { page, viewType } = getState().viewModal;
    const filteredPages = pages.filter(p =>
      viewType == ViewType.Tabular ? p.label != Page.Configure : true
    );
    if (typeof p == 'number') {
      if (page) {
        const idx = filteredPages.map(_ => _.label).indexOf(page);
        p = filteredPages[idx + p].label;
      } else p = null;
    }
    if (p) {
      dispatch(updateViewModalState({ page: p as Page }));
    }
  };

export const setFilter =
  (
    filter: View.FiltersField,
    preApply?: boolean,
    remove?: boolean,
    add?: boolean,
    addAnother?: boolean
  ): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    const { filters, preApplyFilters } = getState().viewModal;
    if (preApply) {
      dispatch(
        updateViewModalState({
          preApplyFilters: updateFilter(
            filter,
            preApplyFilters,
            remove,
            add,
            addAnother
          )
        })
      );
    } else {
      dispatch(
        updateViewModalState({
          filters: updateFilter(filter, filters, remove, add)
        })
      );
    }
  };

export const setFilters =
  (
    filtersIn: View.FiltersField[],
    remove?: boolean,
    preApply?: boolean
  ): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    const { preApplyFilters, filters } = getState().viewModal;
    let newFilters = filtersIn || filters || [];
    let updatedFilters = filtersIn || preApplyFilters || [];
    updatedFilters = updatedFilters.filter(x => x);

    if (preApply) {
      if (remove) {
        updatedFilters = updatedFilters.map(f => {
          return f.isFromParent ? f : { ...f, conditions: undefined };
        });
      }

      dispatch(
        updateViewModalState({
          preApplyFilters: updatedFilters
        })
      );
    } else {
      if (remove) {
        newFilters = newFilters.map(f => {
          return { ...f, conditions: undefined };
        });
      }
      dispatch(
        updateViewModalState({
          filters: newFilters
        })
      );
    }
  };

const updateFilter = (
  filter: View.FiltersField,
  filters: View.FiltersField[] = [],
  remove: boolean,
  add: boolean,
  addAnother?: boolean
) => {
  const parentFilters = filters.filter(f => f.isFromParent);
  const myFilters = filters.filter(f => !f.isFromParent);
  if (!myFilters?.filter(mf => mf.field == filter.field)?.length) {
    filter.uuid = Math.random().toString(10);
  }

  const field = filter.field;
  const uuid = filter.uuid;
  if (remove) {
    return [
      ...parentFilters,
      ...myFilters.filter(
        f => (!uuid && f.field != field) || (!!uuid && f.uuid != uuid)
      )
    ];
  }
  const parentFilter = findOne(parentFilters, { field });
  const myFilter = findOne(myFilters, { field });
  if (myFilter && !addAnother) {
    return [
      ...parentFilters,
      ...myFilters.map(f => {
        if ((f.field == field && !uuid) || (!!uuid && f.uuid == uuid))
          return { ...f, ...filter };
        return f;
      })
    ];
  } else if (!parentFilter) {
    return [...parentFilters, filter, ...myFilters];
  }
  return filters;
};

type VS = Partial<Omit<ViewModalState, 'page'>>;
const setViewSetting =
  (s: VS): ElementAction<Functions, S> =>
  dispatch => {
    dispatch(updateViewModalState(s));
  };

export const useActions = () => {
  const dispatch: Dispatch<any> = useDispatch();
  return {
    openViewModal: (v?: View.Setting, f?: View.Folder) =>
      dispatch(openViewModal(v, f)),
    closeViewModal: () => dispatch(closeViewModal()),
    setViewSetting: (vs: VS) => dispatch(setViewSetting(vs)),
    setFilter: (
      f: View.FiltersField,
      preApply: boolean,
      remove?: boolean,
      add?: boolean,
      addAnother?: boolean
    ) => dispatch(setFilter(f, preApply, remove, add, addAnother)),
    setFilters: (
      filters: View.FiltersField[],
      remove?: boolean,
      preApply?: boolean
    ) => dispatch(setFilters(filters, remove, preApply)),
    goToPage: (ps: Pages, p: string) => dispatch(goToPage(ps, p)),
    nextPage: (ps: Pages) => dispatch(goToPage(ps, 1)),
    prevPage: (ps: Pages) => dispatch(goToPage(ps, -1)),
    deleteView: (v: View.Setting) => dispatch(deleteView(v)),
    cloneView: (
      v: View.Setting,
      folderRefId?: string,
      setSearchParams?: SetURLSearchParams
    ) => dispatch(cloneView(v, folderRefId, setSearchParams)),
    cloneFolder: (folderRefId: string) => dispatch(cloneFolder(folderRefId)),
    save: async () => dispatch(save())
  };
};

type Pages = { label: string }[];
